// frontend/src/components/public/PortfolioView.tsx
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import portfolioService from '../../services/portfolioService';
import { Portfolio } from '../../types/Portfolio';
import BlockRenderer from './BlockRenderer';
import PortfolioHeader from './PortfolioHeader';
import PortfolioFooter from './PortfolioFooter';
import InvestorContextPanel from '../portfolio/InvestorContextPanel';
import { Helmet } from 'react-helmet';

interface PortfolioViewProps {
  slug: string;
}

const PortfolioView: React.FC<PortfolioViewProps> = ({ slug }) => {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await portfolioService.getBySlug(slug);
        setPortfolio(data);
      } catch (err: any) {
        console.error('Error fetching portfolio:', err);
        if (err.response?.status === 404) {
          setError('Portfolio not found');
        } else {
          setError(err.message || 'Failed to load portfolio');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [slug]);

  // Increment view count when portfolio loads successfully
useEffect(() => {
  console.log('🔥 View count effect triggered', { 
    slug: portfolio?.slug, 
    status: portfolio?.status 
  });
  
  if (portfolio?.slug && portfolio.status === 'published') {
    console.log('✅ Calling increment-view');
    fetch(`/api/public/portfolios/${portfolio.slug}/increment-view`)
      .then(r => {
        console.log('📊 Response:', r.status);
        return r.json();
      })
      .then(data => console.log('✅ Data:', data))
      .catch(err => console.error('❌ Failed:', err));
  }
}, [portfolio?.slug, portfolio?.status]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (error || !portfolio) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          gap: 2,
          px: 3
        }}
      >
        <Typography variant="h4" color="error">
          {error || 'Portfolio not found'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The portfolio you're looking for doesn't exist or hasn't been published yet.
        </Typography>
      </Box>
    );
  }

  const theme = {
    primaryColor: portfolio.theme?.primaryColor || '#000000',
    accentColor: portfolio.theme?.accentColor || '#FF0000',
    fontFamily: portfolio.theme?.fontFamily,
    logo: portfolio.theme?.logo
  };

  // Check if contact form exists in sections
  const hasContactForm = portfolio.sections.some(
    section => section.type === 'contact_form'
  );

  // Success state - render portfolio
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff',
        fontFamily: theme.fontFamily || 'inherit'
      }}
    >
      <Helmet>
  <title>{portfolio.meta?.ogTitle || portfolio.title}</title>
  <meta name="description" content={portfolio.meta?.ogDescription || portfolio.description} />

  {/* Open Graph */}
  <meta property="og:title" content={portfolio.meta?.ogTitle || portfolio.title} />
  <meta property="og:description" content={portfolio.meta?.ogDescription || portfolio.description} />
  <meta property="og:image" content={portfolio.meta?.ogImage} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content={window.location.href} />

  {/* Twitter (optional, improves sharing cards) */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={portfolio.meta?.ogTitle || portfolio.title} />
  <meta name="twitter:description" content={portfolio.meta?.ogDescription || portfolio.description} />
  <meta name="twitter:image" content={portfolio.meta?.ogImage} />

  {/* Favicon */}
  {portfolio.meta?.favicon && (
    <link rel="icon" href={portfolio.meta.favicon} />
  )}
</Helmet>
  {/* Header with Logo */}
<PortfolioHeader 
  logo={theme.logo} 
  portfolioTitle={portfolio.title}
  contactName={portfolio.investorContext?.contactName}
  theme={theme}
/>


{/* Portfolio Content */}
<BlockRenderer
  sections={portfolio.sections}
  portfolioTitle={portfolio.title}
  hasContactForm={hasContactForm}
  theme={theme}
/>

      {/* Footer with Logo */}
      <PortfolioFooter 
        logo={theme.logo}
        portfolioTitle={portfolio.title}
        contactName={portfolio.investorContext?.contactName}
        theme={theme}
      />
    </Box>
  );
};

export default PortfolioView;